import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import LogRocket from 'logrocket';

import {
  createQuestion,
  ERROR_SAVE_DRAFT,
  LINEAR_PREBUILD_SURVEY_TYPES,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  SURVEY_TYPES,
  updateQuestion,
} from '@api/instant_survey';

import type { InstantSurvey } from '@api/instant_survey/types';
import type { SamplePricingOption } from '@api/surveys/types';
import {
  shareInstantSurveyWithClientGroup,
  shareInstantSurveyWithUsers,
  surveyStatus,
  viewableSurveyStatuses,
} from '@api/surveys';

import { GATES, LANGUAGE_SCREENER_SCREENER_LABEL, SURVEY_SOURCE_LANGUAGE } from '@constants';
import { getSurveyLanguageSelection } from '@helpers';
import { canPauseSurvey, canResumeSurvey } from '@helpers/surveys';
import {
  ButtonText,
  Card,
  ColumnsIcon,
  HelpIcon,
  Input,
  MinusIcon,
  OverlayTriggerTooltip,
  Pill,
  PlusIcon,
  RepromptIcon,
  SaveIcon,
  ShareIcon,
  Stepper,
  TextArea,
  toast,
  Toolbar,
  TranslateIcon,
} from '@utilities';

import {
  getActiveAdvancedMethodologies,
  getCurrentSamplePrice,
  isLanguageScreenerQuestion,
} from '../../utilities/helpers';
import { payWithCompletes, payWithCredits } from '../../../Checkout/utilities/constants';

import ModalSaveAsDraft from '../ModalSaveAsDraft';
import ModalShare from '../../../../components/ModalShare';
import ModalTitleRequired from '../ModalTitleRequired';
import PauseModal from '../../../Workspace/pages/Surveys/components/Pause/components/PauseModal';
import ResumeModal from '../../../Workspace/pages/Surveys/components/Resume/components/ResumeModal';
import ModalTranslation from '../Panelists/components/ModalTranslation';
import {
  buildLanguageScreenerCreateInput,
  insertLanguageScreenerFirst,
  planLanguageScreenerReconciliation,
} from '../Panelists/components/ModalTranslation/utilities/languageScreener';
import { useSurveyContext } from '../../utilities/context';

import ModalLaunchMethodType from './components/ModalLaunchMethodType';

import styles from './_index.module.scss';

interface SurveyToolbarProps {
  isSaveAsDraftDisabled?: boolean;
  tools?: Array<React.ReactNode>;
}

const areLanguageConfigurationsEqual = (
  previous: InstantSurvey['languageInfo'],
  next: InstantSurvey['languageInfo']
) => {
  const {
    sourceLanguage: previousSource = SURVEY_SOURCE_LANGUAGE,
    translatedLanguages: previousTargets,
  } = getSurveyLanguageSelection(previous, { fallbackSourceLanguage: SURVEY_SOURCE_LANGUAGE });
  const { sourceLanguage: nextSource = SURVEY_SOURCE_LANGUAGE, translatedLanguages: nextTargets } =
    getSurveyLanguageSelection(next, { fallbackSourceLanguage: SURVEY_SOURCE_LANGUAGE });

  return (
    previousSource === nextSource &&
    previousTargets.length === nextTargets.length &&
    previousTargets.every((language, index) => language === nextTargets[index])
  );
};

const ERROR_SAVE_TRANSLATIONS = 'Unable to save translation changes. Please try again.';

const withTranslationReviewState = (
  languageInfo: InstantSurvey['languageInfo'],
  isTranslationReviewed: boolean
): InstantSurvey['languageInfo'] =>
  languageInfo
    ? {
        ...languageInfo,
        isTranslationReviewed,
      }
    : languageInfo;

const SurveyToolbar = ({ isSaveAsDraftDisabled, tools = [] }: SurveyToolbarProps) => {
  const {
    activeGroups,
    activeQuestions,
    activeStep,
    currentQuestionCount,
    currentQuestionnairePrice,
    insightsDocument,
    insightsDocumentType,
    instantSurveyType,
    isAutoSaving,
    isLoadingSubscription,
    isReadOnly,
    isResearchObjectivesTooLong,
    isSaveAsDraftOpen,
    isSavingAsDraft,
    isSubscribed,
    launchMethod,
    maxQuestions,
    persistSurveyDraft,
    setActiveStep,
    setInsightsDocument,
    setIsSaveAsDraftOpen,
    setIsSavingAsDraft,
    setIsTranslationsReviewed,
    setLaunchMethod,
    subscription,
    subscriptionError,
    survey,
    updateSurvey,
  } = useSurveyContext();
  const [isLaunchMethodModalOpen, setIsLaunchMethodModalOpen] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isSurveyTitleModalOpen, setIsSurveyTitleModalOpen] = useState(false);
  const [showModalShare, setShowModalShare] = useState(false);
  const [showModalTranslate, setShowModalTranslate] = useState(false);
  const [showResearchObjectiveEdit, setShowResearchObjectiveEdit] = useState(false);
  const { permissions = {} } = useSelector((state: any) => state?.user?.data);

  const { client } = useStatsigClient();
  const useCalypsoProvider = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);
  const canUseSurveyTranslation =
    client.checkGate(GATES.SURVEY_TRANSLATION) &&
    instantSurveyType?.id !== SURVEY_TYPES.SEQUENTIAL_MONADIC;
  const isNonLinearSurveyEnabled = client.checkGate(GATES.START_SURVEY_NON_LINEAR);

  const isLinearPrebuildSurvey = LINEAR_PREBUILD_SURVEY_TYPES.includes(instantSurveyType?.id);

  const isNonDraftSurvey =
    !!insightsDocument?.status && insightsDocument.status !== surveyStatus.DRAFT;

  const canPause = canPauseSurvey({ permissions, survey: insightsDocument, useCalypsoProvider });
  const canResume = canResumeSurvey({ permissions, survey: insightsDocument, useCalypsoProvider });

  const currentSamplePrice = useMemo<SamplePricingOption>(
    () =>
      // @ts-ignore helpers file is not typed
      getCurrentSamplePrice({
        // @ts-ignore
        samplePricing: insightsDocumentType?.samplePricing,
        survey,
      }),
    [survey]
  );

  const currentSampleCompletesPrice = useMemo<SamplePricingOption>(
    () =>
      // @ts-ignore helpers file is not typed
      getCurrentSamplePrice({
        // @ts-ignore
        samplePricing: insightsDocumentType?.sampleCompletesPricing,
        survey,
      }),
    [insightsDocumentType?.sampleCompletesPricing, survey]
  );

  const handleConfirmMethod = (method: string) => {
    setLaunchMethod(method);
    setIsLaunchMethodModalOpen(false);
  };

  // Serializes language-screener creation so rapid language applies do not create duplicate rows.
  // Callers wait on the shared create/update before treating the selection as committed.
  const languageScreenerCreationPromiseRef = useRef<null | Promise<boolean>>(null);
  const latestLanguageInfoRef = useRef<InstantSurvey['languageInfo']>(null);

  // Sync languageInfo with the managed screener before the modal treats a language change as committed.
  const syncLanguageScreener = async (
    languageInfo: InstantSurvey['languageInfo']
  ): Promise<boolean> => {
    latestLanguageInfoRef.current = languageInfo;
    const plan = planLanguageScreenerReconciliation(survey.questions, languageInfo);

    if (plan.type === 'set') {
      const activeScreener = plan.questions.find(
        (question) =>
          !question._delete &&
          isLanguageScreenerQuestion(question) &&
          typeof question.id === 'number'
      );

      if (!activeScreener) {
        updateSurvey({ languageInfo, questions: plan.questions }, { markDirty: true });
        return true;
      }

      try {
        const updatedScreener = {
          ...(await updateQuestion({ question: activeScreener })),
          properties: {
            ...(activeScreener.properties || {}),
            isLanguageScreener: true,
          },
          screenerLabel: activeScreener.screenerLabel ?? LANGUAGE_SCREENER_SCREENER_LABEL,
        };
        const latestLanguageInfo = latestLanguageInfoRef.current ?? languageInfo;

        if (latestLanguageInfo !== languageInfo) {
          return syncLanguageScreener(latestLanguageInfo);
        }

        updateSurvey(
          {
            languageInfo,
            questions: plan.questions.map((question) =>
              question.id === activeScreener.id ? updatedScreener : question
            ),
          },
          { markDirty: true }
        );
        return true;
      } catch (error) {
        console.error('Error updating language screener question', error);
        toast.error('Unable to update survey languages. Please try again.');
        return false;
      }
    }
    if (plan.type === 'none') {
      updateSurvey({ languageInfo }, { markDirty: true });
      return true;
    }
    if (languageScreenerCreationPromiseRef.current) {
      return languageScreenerCreationPromiseRef.current;
    }

    const creationPromise = (async () => {
      try {
        const createdQuestion = await createQuestion({
          question: buildLanguageScreenerCreateInput(languageInfo, instantSurveyType?.id),
        });
        const created = {
          ...createdQuestion,
          // Force the identity marker in case the backend drops the custom property on create.
          properties: {
            ...(createdQuestion.properties || {}),
            isLanguageScreener: true,
          },
          screenerLabel: LANGUAGE_SCREENER_SCREENER_LABEL,
        };
        // Use the latest selection in case another dropdown Apply happened during creation.
        const latestLanguageInfo = latestLanguageInfoRef.current ?? languageInfo;
        const withCreated = insertLanguageScreenerFirst(created, plan.baseQuestions);
        const reconciled = planLanguageScreenerReconciliation(withCreated, latestLanguageInfo);
        updateSurvey(
          {
            languageInfo: latestLanguageInfo,
            questions: reconciled.type === 'set' ? reconciled.questions : withCreated,
          },
          { markDirty: true }
        );
        return true;
      } catch (error) {
        console.error('Error creating language screener question', error);
        toast.error('Unable to update survey languages. Please try again.');
        return false;
      }
    })();
    languageScreenerCreationPromiseRef.current = creationPromise;

    try {
      return await creationPromise;
    } finally {
      if (languageScreenerCreationPromiseRef.current === creationPromise) {
        languageScreenerCreationPromiseRef.current = null;
      }
    }
  };

  const handleLanguageInfoChange = async (languageInfo: InstantSurvey['languageInfo']) => {
    const previousLanguageInfo = latestLanguageInfoRef.current ?? survey.languageInfo;
    const previousIsTranslationReviewed = Boolean(previousLanguageInfo?.isTranslationReviewed);
    const hasLanguageConfigurationChanged = !areLanguageConfigurationsEqual(
      previousLanguageInfo,
      languageInfo
    );
    const nextLanguageInfo = withTranslationReviewState(
      languageInfo,
      hasLanguageConfigurationChanged ? false : previousIsTranslationReviewed
    );
    const didSync = await syncLanguageScreener(nextLanguageInfo);

    if (!didSync) {
      if (latestLanguageInfoRef.current === nextLanguageInfo) {
        latestLanguageInfoRef.current = previousLanguageInfo;
        setIsTranslationsReviewed(previousIsTranslationReviewed);
      }
      return false;
    }

    if (hasLanguageConfigurationChanged) {
      setIsTranslationsReviewed(false);
    }
    return true;
  };

  const handleTranslationSubmit = async (updates: Partial<InstantSurvey>): Promise<boolean> => {
    // Review/Preview must remain locked until the backend confirms these exact translations.
    setIsTranslationsReviewed(false);
    const reviewedLanguageInfo = withTranslationReviewState(
      survey.languageInfo || latestLanguageInfoRef.current,
      true
    );
    updateSurvey(
      {
        ...updates,
        ...(reviewedLanguageInfo ? { languageInfo: reviewedLanguageInfo } : {}),
      },
      { markDirty: true }
    );

    try {
      // Let the local update commit before the manual save snapshots surveyRef.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const persistResult = await persistSurveyDraft({ reason: 'translation-apply' });
      if (!persistResult) throw new Error('Translation persistence was not confirmed');
    } catch (error) {
      if (reviewedLanguageInfo) {
        updateSurvey(
          {
            languageInfo: withTranslationReviewState(reviewedLanguageInfo, false),
          },
          { markDirty: false }
        );
      }
      console.error('Error saving translation changes', error);
      toast.error(ERROR_SAVE_TRANSLATIONS);
      return false;
    }

    setIsTranslationsReviewed(true);
    setShowModalTranslate(false);
    return true;
  };

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const activeAdvancedMethodologies = getActiveAdvancedMethodologies({ activeQuestions });
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const questionLimit = isVideoSurvey ? 1 : maxQuestions;
  const isSubscriptionAvailable =
    import.meta.env.VITE_LAUNCH_METHOD &&
    isSubscribed &&
    !isLoadingSubscription &&
    !subscriptionError &&
    subscription?.hasStarted === true;

  const advancedMethodologyCompletesCost = activeAdvancedMethodologies.reduce(
    (total, { prices }) => total + prices.completes,
    0
  );
  const advancedMethodologyCreditCost = activeAdvancedMethodologies.reduce(
    (total, { prices }) => total + prices.credits,
    0
  );
  const completesTotal =
    (isVideoSurvey ? (currentSampleCompletesPrice?.amount ?? 0) : currentTotalResponses) +
    ((currentQuestionnairePrice?.questions ?? 0) >= 16 ? 100 : 0) +
    advancedMethodologyCompletesCost;

  const questionnaireCredits = isVideoSurvey ? 0 : (currentQuestionnairePrice?.amount ?? 1);
  const creditsTotal =
    (currentSamplePrice?.amount ?? 0) + advancedMethodologyCreditCost + questionnaireCredits;

  const navigate = useNavigate();
  const handleOpenSaveAsDraft = () => {
    if (!survey.title.trim()) {
      setIsSurveyTitleModalOpen(true);
    } else {
      setIsSaveAsDraftOpen(true);
    }
  };
  const handlePause = () => {
    setInsightsDocument((prev) => ({ ...prev!, isPaused: true }));
    setIsPauseModalOpen(false);
  };
  const handleResume = () => {
    setInsightsDocument((prev) => ({ ...prev!, isPaused: false }));
    setIsResumeModalOpen(false);
  };
  const handleSaveAndExit = async () => {
    setIsSavingAsDraft(true);
    try {
      await persistSurveyDraft({ reason: 'manual' });
    } catch (error) {
      console.error('Error saving survey draft:', error);
      toast.error(ERROR_SAVE_DRAFT);
      setIsSavingAsDraft(false);
      return;
    }

    LogRocket.track('InstantSurvey_SaveAsDraft', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyType.id,
    });
    setIsSavingAsDraft(false);
    navigate({
      pathname: '/dashboard/workspace/surveys',
    });
  };

  return (
    <>
      <Card className={styles['survey-toolbar']} padding="medium">
        <div>
          <Toolbar
            tools={[
              ...tools,
              ...(insightsDocument?.id && !isReadOnly
                ? [
                    ...(canUseSurveyTranslation
                      ? [
                          <OverlayTriggerTooltip content="Translate" key="translate">
                            <button
                              data-testid="translate-button"
                              onClick={() => setShowModalTranslate(true)}
                            >
                              <TranslateIcon />
                            </button>
                          </OverlayTriggerTooltip>,
                        ]
                      : []),
                    <OverlayTriggerTooltip content="Share" key="share">
                      <button data-testid="share-button" onClick={() => setShowModalShare(true)}>
                        <ShareIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(!isReadOnly
                ? [
                    <OverlayTriggerTooltip
                      content={
                        isAutoSaving
                          ? 'Auto-saving in progress...'
                          : isNonDraftSurvey
                            ? 'Save and Exit'
                            : 'Save Draft and Close'
                      }
                      key="save-as-draft"
                    >
                      <button
                        data-testid="save-draft-button"
                        disabled={isSaveAsDraftDisabled || isAutoSaving}
                        onClick={handleOpenSaveAsDraft}
                      >
                        <SaveIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(canPause
                ? [
                    <OverlayTriggerTooltip content="Pause Survey" key="pause-survey">
                      <button
                        data-testid="pause-survey-button"
                        onClick={() => setIsPauseModalOpen(true)}
                      >
                        <ColumnsIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(canResume
                ? [
                    <OverlayTriggerTooltip content="Resume Survey" key="resume-survey">
                      <button
                        data-testid="resume-survey-button"
                        onClick={() => setIsResumeModalOpen(true)}
                      >
                        <RepromptIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),

              ...(instantSurveyType.helpUrl
                ? [
                    <OverlayTriggerTooltip content="Help" key="help">
                      <a
                        aria-label="Help"
                        data-testid="help-link-button"
                        href={instantSurveyType.helpUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <HelpIcon />
                      </a>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
            ]}
          >
            <Input
              className={styles['survey-toolbar-input']}
              isDisabled={isReadOnly}
              maxLength={255}
              onBlur={(title) => updateSurvey({ title: String(title).trim() }, { markDirty: true })}
              onChange={(title) => updateSurvey({ title: String(title) }, { markDirty: true })}
              placeholder="Name your survey (e.g., Heavy vs Light Walmart coffee buyers)"
              value={survey.title}
            />
          </Toolbar>
          {(!isReadOnly || survey.researchObjectives?.length > 0) && (
            <div className={styles['survey-toolbar-research-objectives']}>
              {showResearchObjectiveEdit ? (
                <div>
                  <div className={styles['survey-toolbar-research-objectives-edit']}>
                    <TextArea
                      className={styles['survey-toolbar-research-objectives-textarea']}
                      error={
                        isResearchObjectivesTooLong
                          ? `Research objective is too long, cannot exceed ${RESEARCH_OBJECTIVES_MAX_LENGTH} characters.`
                          : undefined
                      }
                      isDisabled={isReadOnly}
                      onBlur={() => {
                        updateSurvey(
                          { researchObjectives: survey.researchObjectives?.trim() },
                          { markDirty: true }
                        );
                        setShowResearchObjectiveEdit(false);
                      }}
                      onChange={(research_objectives) =>
                        updateSurvey(
                          { researchObjectives: research_objectives },
                          { markDirty: true }
                        )
                      }
                      placeholder="Enter the research objective for this survey."
                      value={survey.researchObjectives || ''}
                    />
                  </div>
                  <div>
                    <ButtonText
                      onClick={() => {
                        setShowResearchObjectiveEdit(false);
                      }}
                    >
                      <MinusIcon />
                      <span>
                        Hide <strong>Objective</strong>
                      </span>
                    </ButtonText>
                  </div>
                </div>
              ) : (
                <ButtonText onClick={() => setShowResearchObjectiveEdit(true)}>
                  <PlusIcon />
                  <span>
                    Show <strong>Objective</strong>
                  </span>
                </ButtonText>
              )}
            </div>
          )}
        </div>

        <div className={styles['survey-toolbar-info']}>
          <div>
            <p>
              <strong>{activeGroups.length}</strong> Quota Group
              {activeGroups.length > 1 ? 's' : ''}
            </p>
            <p>
              <strong>{currentTotalResponses}</strong> Total Responses
            </p>
            <p>
              <strong>{currentQuestionCount + '/' + questionLimit}</strong>{' '}
              {`Question${questionLimit === 1 ? '' : 's'}`}
            </p>
          </div>
          <div>
            {isSubscriptionAvailable ? (
              <>
                <button
                  className={styles['survey-toolbar-launch-method-button']}
                  disabled={isNonDraftSurvey}
                  onClick={() => setIsLaunchMethodModalOpen(true)}
                >
                  {isNonDraftSurvey ? 'Launched with' : 'Launch with'}
                </button>
                &nbsp;
                <span>
                  <strong>
                    {launchMethod === payWithCompletes.key
                      ? `${completesTotal} Completes`
                      : launchMethod === payWithCredits.key &&
                          creditsTotal !== undefined &&
                          !isNaN(creditsTotal)
                        ? `${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`
                        : 'N/A'}
                  </strong>
                </span>
              </>
            ) : (
              <>
                Total Cost:&nbsp;
                {currentSamplePrice ? (
                  <Pill
                    isCompleted
                    value={`${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`}
                  />
                ) : (
                  <span>N/A</span>
                )}
              </>
            )}
          </div>
        </div>
        <Stepper
          activeIndex={activeStep}
          navigationType={
            isNonLinearSurveyEnabled && !isLinearPrebuildSurvey ? 'non-linear' : 'linear'
          }
          onChange={(index) => setActiveStep(index)}
          steps={[
            {
              isComplete: activeStep > 0,
              label: 'Select Panelists',
            },
            {
              isComplete: survey.questions.length >= 1,
              label: 'Create Questionnaire',
            },
            {
              label: 'Review Survey',
            },
          ]}
        />
      </Card>
      {isPauseModalOpen && (
        <PauseModal
          id={insightsDocument!.id}
          onClose={() => setIsPauseModalOpen(false)}
          onSubmit={handlePause}
        />
      )}
      {isResumeModalOpen && (
        <ResumeModal
          id={insightsDocument!.id}
          onClose={() => setIsResumeModalOpen(false)}
          onSubmit={handleResume}
        />
      )}
      {isSaveAsDraftOpen && (
        <ModalSaveAsDraft
          isActive
          isNonDraftSurvey={isNonDraftSurvey}
          isSaving={isSavingAsDraft}
          onClose={() => setIsSaveAsDraftOpen(false)}
          onSubmit={handleSaveAndExit}
        />
      )}
      {isSurveyTitleModalOpen && (
        <ModalTitleRequired onClose={() => setIsSurveyTitleModalOpen(false)} />
      )}
      {isLaunchMethodModalOpen && (
        <ModalLaunchMethodType
          creditsTotal={creditsTotal}
          isOpen={isLaunchMethodModalOpen}
          launchMethod={launchMethod}
          onClose={() => setIsLaunchMethodModalOpen(false)}
          onConfirm={handleConfirmMethod}
          setLaunchMethod={setLaunchMethod}
        />
      )}
      {showModalShare && insightsDocument?.id && (
        // @ts-ignore ModalShare is not typed yet
        <ModalShare
          hasAllColleagues
          hasFindColleagues
          hasSharePublicly={viewableSurveyStatuses.includes(insightsDocument.status)}
          ids={[Number(insightsDocument.id)]}
          isSurvey
          setShowModalShare={setShowModalShare}
          shareItemWithClients={shareInstantSurveyWithClientGroup}
          shareItemWithUsers={shareInstantSurveyWithUsers}
        />
      )}
      {canUseSurveyTranslation && showModalTranslate && insightsDocument?.id && (
        <ModalTranslation
          onClose={() => setShowModalTranslate(false)}
          onLanguageInfoChange={handleLanguageInfoChange}
          onSubmit={handleTranslationSubmit}
          survey={survey}
        />
      )}
    </>
  );
};

export default SurveyToolbar;
